/* eslint-disable @typescript-eslint/no-explicit-any */
import { YandexMap } from 'application/shared/yandex-map'
import { Quest, QuestLocation } from '../../types.d'
import { createMapQuestTemplate } from 'application/quests/_quest'
import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll'
import { pluralizeWord } from 'application/utils/helpers'

const questWordForms: [string, string, string] = ['квест', 'квеста', 'квестов']

// Максимальный зум карты после подгонки под метки, чтобы карта не приближалась
// к упору, когда квесты находятся рядом друг с другом или их всего одна-две
const MAX_ZOOM_AFTER_FIT = 15

export class CatalogMap {
  private container: HTMLElement
  private loaded: boolean = false
  private map: any
  private placemarks: any[] = []
  private mapContainer: HTMLElement
  private quests: Quest[] = []
  private locations: QuestLocation[] = []
  private panel: HTMLElement
  private matchMedia: MediaQueryList
  private isPanelOpen: boolean = false

  /**
   * Создает экземпляр карты каталога квестов
   * @param {HTMLElement} container - HTML-контейнер для карты
   * @param {Quest[]} quests - Массив квестов для отображения на карте
   */
  constructor(container: HTMLElement, quests: Quest[] = []) {
    this.container = container
    this.mapContainer = container.querySelector(
      '[data-quests-map-container]'
    ) as HTMLElement
    this.quests = quests

    this.init()
  }

  /**
   * Инициализирует компонент карты, собирает локации и устанавливает обработчики событий
   */
  private init(): void {
    this.panel = this.container.querySelector(
      '[data-quests-map-panel]'
    ) as HTMLElement
    this.locations = this.collectLocations(this.quests)

    this.matchMedia = window.matchMedia('(max-width: 767px)')

    this.container.addEventListener('click', this.onPanelClick)
    this.matchMedia.addEventListener('change', this.onMediaChange)
  }

  /**
   * Обновляет метки и закрывает панель с устаревшими результатами
   * @param {Quest[]} quests - Полный отфильтрованный набор квестов
   */
  public updateQuests(quests: Quest[]): void {
    this.closePanel()
    this.quests = quests
    this.locations = this.collectLocations(quests)
    if (this.map) this.renderPlacemarks()
  }

  /** Показывает карту, инициализируя ее при первом вызове */
  public show(): void {
    if (!this.loaded) {
      this.loadMap()
    }
  }

  /**
   * Асинхронно загружает API Яндекс.Карт и инициализирует карту с контролами
   */
  private async loadMap(): Promise<void> {
    const preloader =
      this.container.querySelector<HTMLElement>('[data-preloader]')
    preloader?.classList.add('is-active')

    await YandexMap.getAPI()

    const init = () => {
      this.map = new window.ymaps.Map(this.mapContainer, {
        center: [55.73, 37.75],
        zoom: 10,
        controls: []
      })

      // Отключаем zoom колёсиком мыши по умолчанию
      this.map.behaviors.disable('scrollZoom')

      // Включаем zoom только при нажатом Ctrl
      this.mapContainer.addEventListener('wheel', (e: WheelEvent) => {
        if (e.ctrlKey) {
          this.map.behaviors.enable('scrollZoom')
        } else {
          this.map.behaviors.disable('scrollZoom')
        }
      })

      // Добавляем контролы
      const geolocation = new window.ymaps.control.GeolocationControl({
        data: { image: '/images/svg/geolocation.svg' }
      })
      this.map.controls.add(geolocation)

      const zoom = new window.ymaps.control.ZoomControl()
      this.map.controls.add(zoom)

      this.renderPlacemarks()
      preloader?.classList.remove('is-active')
    }

    const ymaps = window.ymaps
    if (ymaps && ymaps.ready) {
      ymaps.ready(init)
    }

    this.loaded = true
  }

  /**
   * Создает метки для всех локаций на карте
   */
  private initPlacemarks(): void {
    if (!this.map) return

    this.placemarks = []

    this.locations.forEach((location) => {
      const { id, latLng } = location
      const questsCount = this.filterQuests(id).length
      const placemark = new window.ymaps.Placemark(
        latLng,
        {
          id,
          balloonContentBody: this.createBalloonContent(location, questsCount)
        },
        {
          iconLayout: 'default#image',
          iconImageHref: this.markerIcon(id),
          iconImageSize: [40, 40],
          iconImageOffset: [-20, -20],
          hideIconOnBalloonOpen: false
        }
      )

      placemark.events.add('click', this.listenPlacemarkClick)
      placemark.events.add('mouseenter', this.listenPlacemarkMouseEnter)
      placemark.events.add('mouseleave', this.listenPlacemarkMouseLeave)
      this.placemarks.push(placemark)
    })
  }

  /**
   * Отрисовывает метки на карте и подгоняет границы карты под все объекты
   */
  private renderPlacemarks(): void {
    this.map.geoObjects.removeAll()

    this.initPlacemarks()

    this.placemarks.forEach((placemark) => {
      this.map.geoObjects.add(placemark)
    })

    const bounds = this.map.geoObjects.getBounds()
    if (bounds) {
      this.map
        .setBounds(bounds)
        .then(() =>
          this.map.getZoom() > MAX_ZOOM_AFTER_FIT
            ? this.map.setZoom(MAX_ZOOM_AFTER_FIT)
            : null
        )
        .catch((error: unknown) => {
          console.error(error)
        })
    }
  }

  /**
   * Собирает уникальные локации из массива квестов
   * @param {Quest[]} quests - Массив квестов
   * @return {QuestLocation[]} Массив уникальных локаций с координатами
   */
  private collectLocations(quests: Quest[]): QuestLocation[] {
    const locationsMap: Record<string, QuestLocation> = {}

    quests.forEach((quest) => {
      const location = quest.location
      if (location?.latLng) {
        locationsMap[location.id] = location
      }
    })

    return Object.values(locationsMap)
  }

  /**
   * Обрабатывает клик по метке на карте, меняет иконку и показывает панель с квестами
   * @param {any} e - Событие клика на метке
   */
  private listenPlacemarkClick = (e: any) => {
    const type = e.get('type')
    if (type !== 'click') return

    this.resetMarkerIcons()

    const target = e.get('target')
    const locationId = target.properties.get('id')
    target.options.set('iconImageHref', this.markerIcon(locationId, true))

    this.showQuestsPanel(locationId)
  }

  private listenPlacemarkMouseEnter = (e: any) => {
    const target = e.get('target')
    target.balloon.open()
  }

  private listenPlacemarkMouseLeave = (e: any) => {
    const target = e.get('target')
    target.balloon.close()
  }

  private createBalloonContent(
    location: QuestLocation,
    questsCount: number
  ): string {
    const title = location.metro || 'Локация'
    const plural = pluralizeWord(questsCount, questWordForms)

    return `
      <div class="quests-map-balloon">
        <div class="quests-map-balloon__title">${title}</div>
        <div class="quests-map-balloon__address">${location.address}</div>
        <div class="quests-map-balloon__count">${questsCount} ${plural}</div>
      </div>
    `
  }

  /**
   * Показывает боковую панель с квестами для выбранной локации
   * @param {string} locationId - Идентификатор локации
   */
  private showQuestsPanel(locationId: string): void {
    const location = this.locations.find((l) => l.id === locationId)
    if (!location) return

    const panelTitle =
      this.panel.querySelector<HTMLElement>('[data-panel-title]')

    if (panelTitle) {
      panelTitle.textContent = location.metro
    }

    const panelMetro =
      this.panel.querySelector<HTMLElement>('[data-panel-metro]')

    if (panelMetro) {
      if (location.metro) {
        panelMetro.style.setProperty(
          '--branch-color',
          `#${location.stations[0].color}`
        )
        panelMetro.textContent = `${location.metro} ${location.address}`
      } else {
        panelMetro.textContent = location.address
      }
    }

    const filteredQuests = this.filterQuests(locationId)
    this.renderQuestsList(filteredQuests)

    this.panel.classList.add('is-visible')
    this.isPanelOpen = true

    if (this.matchMedia.matches) {
      disablePageScroll()
    }
  }

  /**
   * Обрабатывает клик внутри панели для закрытия при нажатии на кнопку закрытия
   * @param {MouseEvent} e - Событие клика мыши
   */
  private onPanelClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement
    if (target.closest('[data-panel-close]')) {
      this.closePanel()
    }
  }

  /**
   * Закрывает боковую панель с квестами и возвращает иконки меток в исходное состояние
   */
  private closePanel(): void {
    if (!this.isPanelOpen) return

    this.panel.classList.remove('is-visible')
    this.isPanelOpen = false

    this.resetMarkerIcons()

    if (this.matchMedia.matches) {
      enablePageScroll()
    }
  }

  /**
   * Фильтрует квесты по идентификатору локации
   * @param {string} locationId - Идентификатор локации
   * @return {Quest[]} Массив квестов в указанной локации
   */
  private filterQuests(locationId: string): Quest[] {
    return this.quests.filter((quest) => quest.location?.id === locationId)
  }

  /**
   * Иконка метки: локация с квестом Оригинала Клаустрофобии — ключ,
   * остальные — обычная синяя метка. Активная (открытая панель) — filled.
   *
   * @param {string} locationId - Идентификатор локации
   * @param {boolean} active - Метка выбрана
   * @return {string} Адрес картинки
   */
  private markerIcon(locationId: string, active = false): string {
    const original = this.filterQuests(locationId).some(
      (quest) => quest.original === true
    )
    const name = `marker${original ? '-original' : ''}${active ? '-filled' : ''}`
    return `/images/wizard/map/${name}.svg`
  }

  private resetMarkerIcons(): void {
    this.placemarks.forEach((placemark) => {
      placemark.options.set(
        'iconImageHref',
        this.markerIcon(placemark.properties.get('id'))
      )
    })
  }

  /**
   * Отрисовывает список квестов в боковой панели
   * @param {Quest[]} quests - Массив квестов для отображения
   */
  private renderQuestsList(quests: Quest[]): void {
    const questsList =
      this.panel.querySelector<HTMLElement>('[data-quests-list]')
    if (!questsList) return

    questsList.innerHTML = quests
      .map(
        (quest) => `
          <li class="quests-map__item" data-quest-id="${quest.id}">
            ${createMapQuestTemplate(quest)}
          </li>
        `
      )
      .join('')

    questsList.scrollTop = 0
  }

  /**
   * Обрабатывает изменение медиа-запроса для адаптивного поведения панели
   */
  private onMediaChange = () => {
    if (!this.matchMedia.matches && this.isPanelOpen) {
      enablePageScroll()
    }

    this.closePanel()
  }
}
